import { TokenProvider, useTokenValues } from '@jds/react-engine';

import { ButtonProps } from '../jdsbutton/logic';
import { IPROP_MAP } from '../../generators/modifyProps';
import { JDSPromoCardProps } from './generated';
import { ReactElement } from 'react';
import { TextProps } from '../jdstext/logic';
import { convertTextToComponent } from '../../utils/componenthelpers';

const legacySizes = ['xs', 's', 'm', 'l'];
type legacySizesType = (typeof legacySizes)[number];

export interface PromoCardProps
  extends Omit<
    JDSPromoCardProps,
    'size' | 'children' | 'footerPrefix' | 'primaryCTA' | 'image'
  > {
  /**
   * Size is detemined by contentBlockSize.
   * @deprecated
   */
  contentBlockSize?: 'default' | 'compact';
  /**
   *
   * Determines the size of the PromoCard. Size dictates the text and spacing.
   */
  size?: legacySizesType | JDSPromoCardProps['size'];
  /**
   *Accepts description as string. JSX.Element is also allowed in case you have anchor tags inside your richtext.
   */
  description?: string;
  /**
   * Image HTML tags. e.g. `<img>`
   */
  image?: string | JSX.Element;
  /**
   * Please see `Link>LinkType`. The `button={}` parameter can be sent to overwrite
   * default button behaviours. E.g. different kind, destructive, with icons etc.
   */
  primaryCTA?:
    | string
    | {
        /**
         * @deprecated pass string to `primaryCTA`
         */
        title: string;
        /**
         * @deprecated use `onPrimaryClick`
         */
        onClick?: ButtonProps['onClick'];
        /**
         * @deprecated
         */
        _button?: ButtonProps;
      };
  /**
   * Image HTML tags. e.g. `<img>`
   */
  children?: ReactElement;
  /**
   * footerPrefix corresponds to the left part of the footer. Incase of a String, a text with style body-xxs with grey-100 color is shown
   * Passing string is deprecated. Please pass a jds text component
   */
  footerPrefix?: string | ReactElement<TextProps>;
}

export const PROP_MAP: IPROP_MAP<PromoCardProps> = (props) => {
  return {
    size: {
      defaultValue: 'xtra-small',
      variations: {
        s: {
          size: 'small'
        },
        m: {
          size: 'medium'
        },
        xs: {
          size: 'xtra-small'
        },
        l: {
          size: 'large'
        }
      }
    },
    image: {
      properties: {
        image: props.image?.props?.src || props.image
      }
    }
  };
};

export const withLogic =
  (Component: React.ComponentType<PromoCardProps>): React.FC<PromoCardProps> =>
  (props: PromoCardProps) => {
    const { footerPrefix, footerSuffix, ...rest } = props;
    const tokens = useTokenValues();
    const formatImageName = (imagePath = props.src || '') => {
      const parts = imagePath.split('/');
      const imageName = parts.length > 0 ? parts[parts.length - 1] : '';

      if (imageName) {
        const formattedName =
          imageName
            .replace(/\.\w+$/, '')
            .replace(/([A-Z])/g, ' $1')
            .toLowerCase() + ' image';
        return formattedName;
      }
      return 'Image name not found';
    };
    return (
      <TokenProvider value={{ ...tokens, mode: 'bold' }}>
        <Component
          {...{
            ...rest,
            _hasChildren: Boolean(rest.children),
            _hasFooter: Boolean(footerPrefix || footerSuffix),
            children: convertTextToComponent(rest.children),
            footerPrefix: convertTextToComponent(footerPrefix),
            footerSuffix: convertTextToComponent(footerSuffix),
            _imageAriaLabel: formatImageName(props.image)
          }}
        />
      </TokenProvider>
    );
  };
